import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Collapse,
  Card,
  Row,
  Col,
  Steps,
  message,
  Select,
  Modal,
  Table,
  Space,
  Popconfirm,
  Tag,
  Checkbox,
} from "antd";
import {
  MinusCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import Layout from "../layout/AppLayout";
import {
  fetchMarketplaceList,
  CreateNewEndPoint,
  GetSoverOnboarding,
  DeleteSoverOnboarding,
} from "../services/accessAPI";
import decodeJWTtenant from "../services/decodeJWTtenant";
import SessionManager from "../auth/sessionmanager";
import "./CreateNewEndpoints.css";
import decodeJWT from "../services/decodeJWT";

const { TextArea } = Input;
const { Option } = Select;
const { Step } = Steps;

const CreateNewEndpoints = () => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stepValidation, setStepValidation] = useState({
    0: false, // Basic Info
    1: false, // Solving Process
    2: false, // Endpoints
    3: false, // Location
    4: false, // Deployment
  });

  // State for unique options
  const [uniqueCategory, setUniqueCategory] = useState([]);
  const [uniqueCategoryDescription, setUniqueCategoryDescription] = useState([]);
  const [uniqueInterfaceFromOptions, setUniqueInterfaceFromOptions] = useState([]);
  const [uniqueInterfaceToOptions, setUniqueInterfaceToOptions] = useState([]);
  const [uniqueRealWorldProblem, setUniqueRealWorldProblem] = useState([]);

  // State for form selections
  const [category, setCategory] = useState(null);
  const [categoryDescription, setCategoryDescription] = useState(null);
  const token = SessionManager.getToken?.();
  const tenant = decodeJWTtenant(token);
  const role = decodeJWT(token);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [onboardedSolvers, setOnboardedSolvers] = useState([]);
  const [filteredSolvers, setFilteredSolvers] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [arrayFormatStates, setArrayFormatStates] = useState({});
  const [arrayFormatOutputStates, setArrayFormatOutputStates] = useState({});

  // Step definitions
  const steps = [
    {
      title: 'Basic Info',
      description: 'General information about the solver',
      icon: <CheckCircleOutlined />,
    },
    {
      title: 'Solving Process',
      description: 'Define solving processes and interfaces',
      icon: <CheckCircleOutlined />,
    },
    {
      title: 'Endpoints',
      description: 'Configure API endpoints',
      icon: <CheckCircleOutlined />,
    },
    {
      title: 'Location',
      description: 'Set deployment locations',
      icon: <CheckCircleOutlined />,
    },
    {
      title: 'Deployment',
      description: 'Docker and deployment settings',
      icon: <CheckCircleOutlined />,
    },
  ];

  // Load API data
  useEffect(() => {
    const loadApiConfig = async () => {
      try {
        const apiList = await fetchMarketplaceList();
        if (apiList) {
          setData(apiList);
        } else {
          message.warning("No data available.");
        }
      } catch (error) {
        console.error("Error fetching API list:", error);
        message.error("Failed to load marketplace data.");
      } finally {
        setLoading(false);
      }
    };
    loadApiConfig();
  }, []);

  // Helper function to normalize and deduplicate strings
  const normalizeAndDeduplicate = (values) => {
    const uniqueValues = new Set();

    values.forEach((value, index) => {
      if (value && typeof value === "string") {
        // Remove all non-printable characters, normalize spaces, trim, and uppercase
        const normalized = value
          .replace(/[\u200B-\u200D\uFEFF]/g, "")
          .trim()
          .toLowerCase()
          .replace(/^[a-z]/, (firstChar) => firstChar.toUpperCase());

        if (normalized) {
          uniqueValues.add(normalized);
        }
      }
    });
    const result = [...uniqueValues].sort();
    return result;
  };

  useEffect(() => {
    const categoryValues = [];
    const categoryDescriptionValues = [];
    const interfaceToValues = [];
    const interfaceFromValues = [];
    const realWorldProblemValues = [];

    data.forEach((item) => {
      // Collect category values
      if (item.category) {
        categoryValues.push(item.category);
      }

      // Collect category description values
      if (item.categoryDescription) {
        categoryDescriptionValues.push(item.categoryDescription);
      }

      // Process solvingProcesses array
      if (item.solvingProcesses && Array.isArray(item.solvingProcesses)) {
        item.solvingProcesses.forEach((process) => {
          // Process intermodalInterfaces
          if (process.intermodalInterfaces && Array.isArray(process.intermodalInterfaces)) {
            process.intermodalInterfaces.forEach((interfaceObj) => {
              if (interfaceObj.interfaceTo) {
                interfaceToValues.push(interfaceObj.interfaceTo);
              }
              if (interfaceObj.interfaceFrom) {
                interfaceFromValues.push(interfaceObj.interfaceFrom);
              }
            });
          }

          // Process realWorldProblems
          if (process.realWorldProblems && Array.isArray(process.realWorldProblems)) {
            process.realWorldProblems.forEach((problemObj) => {
              if (problemObj.realWorldProblem) {
                realWorldProblemValues.push(problemObj.realWorldProblem);
              }
            });
          }
        });
      }
    });

    // Normalize and deduplicate all collected values
    setUniqueCategory(normalizeAndDeduplicate(categoryValues));
    setUniqueCategoryDescription(normalizeAndDeduplicate(categoryDescriptionValues));
    setUniqueInterfaceFromOptions(normalizeAndDeduplicate(interfaceFromValues));
    setUniqueInterfaceToOptions(normalizeAndDeduplicate(interfaceToValues));
    setUniqueRealWorldProblem(normalizeAndDeduplicate(realWorldProblemValues));
  }, [data]);

  useEffect(() => {
    if (uniqueCategoryDescription.length > 0) {
      form.setFieldsValue({
        categoryDescription: "",
      });
    }
  }, [uniqueCategoryDescription]);

  // Validation function for each step
  const validateCurrentStep = async (step) => {
    let fieldsToValidate = [];
    
    switch (step) {
      case 0: // Basic Info
        fieldsToValidate = ['version', 'developer', 'license', 'category', 'description'];
        break;
      case 1: // Solving Process - optional, so always valid
        return true;
      case 2: // Endpoints
        try {
          const values = await form.validateFields();
          if (!values.endpoints || values.endpoints.length < 1) {
            return false;
          }
          // Validate each endpoint has required fields
          for (let endpoint of values.endpoints) {
            if (!endpoint.httpMethod || !endpoint.description) {
              return false;
            }
            if (!endpoint.expectedOutput || endpoint.expectedOutput.length < 1) {
              return false;
            }
          }
          return true;
        } catch {
          return false;
        }
      case 3: // Location
        try {
          const values = await form.validateFields();
          if (!values.locations || values.locations.length < 1) {
            return false;
          }
          return true;
        } catch {
          return false;
        }
      case 4: // Deployment
        fieldsToValidate = ['deployment.dockerImage', 'deployment.version', 'deployment.containerPort'];
        break;
      default:
        return true;
    }

    try {
      await form.validateFields(fieldsToValidate);
      return true;
    } catch {
      return false;
    }
  };

  // Handle step navigation
  const handleNext = async () => {
    const isValid = await validateCurrentStep(currentStep);
    if (isValid) {
      setStepValidation(prev => ({ ...prev, [currentStep]: true }));
      setCurrentStep(prev => prev + 1);
    } else {
      message.error('Please fill in all required fields before proceeding.');
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

const handleSubmit = async () => {
  try {
    const allValues = form.getFieldsValue(true); 
    console.log("Form values: ", allValues);

    // Clean solving processes - remove empty processes and undefined values
    if (allValues.solvingProcesses) {
      allValues.solvingProcesses = allValues.solvingProcesses
        .map(process => {
          // Skip if process is undefined or null
          if (!process) return null;
          
          // Ensure name is always present, even if empty
          if (!process.name) {
            process.name = "";
          }
          
          // Clean intermodalInterfaces - remove undefined/empty entries
          if (process.intermodalInterfaces) {
            process.intermodalInterfaces = process.intermodalInterfaces
              .filter(iface => iface && (iface.interfaceFrom || iface.interfaceTo))
              .map(iface => ({
                interfaceFrom: iface.interfaceFrom || undefined,
                interfaceTo: iface.interfaceTo || undefined
              }));
            
            // If no valid interfaces, set to empty array
            if (process.intermodalInterfaces.length === 0) {
              process.intermodalInterfaces = [];
            }
          } else {
            process.intermodalInterfaces = [];
          }
          
          // Clean realWorldProblems - remove undefined/empty entries
          if (process.realWorldProblems) {
            process.realWorldProblems = process.realWorldProblems
              .filter(problem => problem && problem.realWorldProblem)
              .map(problem => ({
                realWorldProblem: problem.realWorldProblem
              }));
            
            // If no valid problems, set to empty array
            if (process.realWorldProblems.length === 0) {
              process.realWorldProblems = [];
            }
          } else {
            process.realWorldProblems = [];
          }
          
          // Check if process has any meaningful data (now name is always present)
          const hasName = process.name && process.name.trim();
          const hasInterfaces = process.intermodalInterfaces.length > 0;
          const hasProblems = process.realWorldProblems.length > 0;
          
          // If process has no data at all (empty name, no interfaces, no problems), mark for removal
          if (!hasName && !hasInterfaces && !hasProblems) {
            return null;
          }
          
          return process;
        })
        .filter(process => process !== null); // Remove null entries
      
      // If no valid processes, set to empty array
      if (allValues.solvingProcesses.length === 0) {
        allValues.solvingProcesses = [];
      }
    } else {
      // Ensure solvingProcesses is always an array, even if undefined
      allValues.solvingProcesses = [];
    }

    await form.validateFields(); 
    await CreateNewEndPoint(allValues);

    form.resetFields();
    setCurrentStep(0);
    setStepValidation({
      0: false,
      1: false,
      2: false,
      3: false,
      4: false,
    });
    message.success("Created successfully");
  } catch (error) {
    message.error(error.message || "Failed to create endpoint");
    console.error("API Error:", error);
  }
};

  // Step content components
  const renderBasicInfo = () => (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Row gutter={16}>
        {[
          ["version", "Version"],
          ["developer", "Developer"],
          ["license", "License"],
        ].map(([key, label]) => (
          <Col span={12} key={key}>
            <Form.Item name={key} label={label} rules={[{ required: true, message: `${label} is required` }]}>
              <Input />
            </Form.Item>
          </Col>
        ))}
      </Row>

      <Form.Item name="category" label="Category" rules={[{ required: true, message: "Category is required" }]}>
        <Select
          value={category}
          onChange={(value) => setCategory(value)}
          placeholder="Select Category"
        >
          {uniqueCategory.map((categoryItem) => (
            <Option key={categoryItem} value={categoryItem}>
              {categoryItem}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item name="categoryDescription" hidden initialValue={""}>
        <Input type="hidden" />
      </Form.Item>

      <Form.Item name="tenants" hidden initialValue={tenant ? [tenant] : []}>
        <Input type="hidden" />
      </Form.Item>

      <Form.Item
        name="description"
        label="Description"
        rules={[{ required: true, message: "Description is required" }]}
      >
        <TextArea rows={4} />
      </Form.Item>

      <Form.Item name="makePublic" label="Make Public" valuePropName="checked">
        <Checkbox />
      </Form.Item>
    </div>
  );

  const renderSolvingProcess = () => (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Form.List name="solvingProcesses">
        {(processFields, { add: addProcess, remove: removeProcess }) => (
          <>
            <div style={{ marginBottom: 16 }}>
              <h3>Solving Processes <span style={{ fontSize: 14, color: '#666' }}>(Optional)</span></h3>
              <p style={{ color: '#666', marginBottom: 16 }}>
                Define the solving processes, interfaces, and real-world problems this solver addresses.
              </p>
            </div>
            {processFields.map(({ key, name }) => (
              <Card key={key} style={{ marginBottom: 16 , height:'auto' }} title={`Process #${name + 1}`}>
                <Row gutter={16}>
                  <Col span={20}>
                    <Form.Item name={[name, "name"]} label="Process Name">
                      <Input placeholder="Enter process name" />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Button danger onClick={() => removeProcess(name)}>
                      Remove
                    </Button>
                  </Col>
                </Row>

                <Form.List name={[name, "intermodalInterfaces"]}>
                  {(interfaceFields, { add: addInterface, remove: removeInterface }) => (
                    <>
                      <h4>Intermodal Interfaces</h4>
                      {interfaceFields.map(({ key: interfaceKey, name: interfaceName }) => (
                        <Row gutter={8} key={interfaceKey}>
                          <Col span={10}>
                            <Form.Item name={[interfaceName, "interfaceFrom"]}>
                              <Select placeholder="Interface From">
                                {uniqueInterfaceFromOptions.map((interfaceFromItem, idx) => (
                                  <Option key={idx} value={interfaceFromItem}>
                                    {interfaceFromItem}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={10}>
                            <Form.Item name={[interfaceName, "interfaceTo"]}>
                              <Select placeholder="Interface To">
                                {uniqueInterfaceToOptions.map((interfaceToItem, idx) => (
                                  <Option key={idx} value={interfaceToItem}>
                                    {interfaceToItem}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Button
                              danger
                              icon={<MinusCircleOutlined />}
                              onClick={() => removeInterface(interfaceName)}
                            />
                          </Col>
                        </Row>
                      ))}
                      <Form.Item>
                        <Button type="dashed" onClick={() => addInterface()} icon={<PlusOutlined />} block>
                          Add Interface
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>

                <Form.List name={[name, "realWorldProblems"]}>
                  {(problemFields, { add: addProblem, remove: removeProblem }) => (
                    <>
                      <h4>Real World Problems</h4>
                      {problemFields.map(({ key: problemKey, name: problemName }) => (
                        <Row gutter={8} key={problemKey}>
                          <Col span={20}>
                            <Form.Item name={[problemName, "realWorldProblem"]}>
                              <Select
                                placeholder="Select or type problem"
                                showSearch
                                allowClear
                                filterOption={(input, option) =>
                                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                              >
                                {uniqueRealWorldProblem.map((problem, idx) => (
                                  <Option key={idx} value={problem}>
                                    {problem}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Button
                              danger
                              icon={<MinusCircleOutlined />}
                              onClick={() => removeProblem(problemName)}
                            />
                          </Col>
                        </Row>
                      ))}
                      <Form.Item>
                        <Button type="dashed" onClick={() => addProblem()} icon={<PlusOutlined />} block>
                          Add Problem
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </Card>
            ))}
            <Form.Item>
              <Button type="dashed" onClick={() => addProcess()} icon={<PlusOutlined />} block>
                Add Solving Process
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    </div>
  );

  const renderEndpoints = () => (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <h3>API Endpoints <span style={{ color: 'red' }}>*</span></h3>
        <p style={{ color: '#666', marginBottom: 16 }}>
          Configure the API endpoints for your solver. At least one endpoint is required.
        </p>
      </div>
      
      <Form.List
        name="endpoints"
        rules={[
          {
            validator: async (_, value) => {
              if (!value || value.length < 1) {
                return Promise.reject(new Error("At least one endpoint is required"));
              }
            },
          },
        ]}
      >
        {(fields, { add, remove }) => (
          <>
            <Collapse
              accordion
              items={fields.map((field, index) => ({
                key: field.key,
                label: `Endpoint #${index + 1}`,
                extra: (
                  <Button
                    danger
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(field.name);
                    }}
                  >
                    Remove
                  </Button>
                ),
                children: (
                  <>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item
                          name={[field.name, "httpMethod"]}
                          label="HTTP Method"
                          rules={[{ required: true, message: "HTTP method is required" }]}
                        >
                          <Select placeholder="Select Method">
                            <Option value="GET">GET</Option>
                            <Option value="POST">POST</Option>
                            <Option value="PUT">PUT</Option>
                            <Option value="DELETE">DELETE</Option>
                            <Option value="PATCH">PATCH</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={16}>
                        <Form.Item name={[field.name, "path"]} label="Path">
                          <Input placeholder="/my-endpoint" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item
                      name={[field.name, "description"]}
                      label="Description"
                      rules={[{ required: true, message: "Description is required" }]}
                    >
                      <Input.TextArea rows={2} />
                    </Form.Item>

                    <Card
                      type="inner"
                      title="Expected Input (optional - set to null if no input required)"
                      style={{ marginBottom: 12, overflow: "auto" , height:'auto' }}
                    >
                      <Form.List name={[field.name, "expectedInput"]}>
                        {(inputFields, { add: addInput, remove: removeInput }) => (
                          <>
                            {inputFields.map(({ key: inputKey, name: inputName }) => (
                              <Row gutter={16} key={inputKey}>
                                <Col span={4}>
                                  <Form.Item name={[inputName, "name"]} rules={[{ required: true }]}>
                                    <Input placeholder="Name" />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item name={[inputName, "format"]} rules={[{ required: true }]}>
                                    <Input placeholder="Format" />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item name={[inputName, "in"]} rules={[{ required: true }]}>
                                    <Select placeholder="In">
                                      <Option value="query">query</Option>
                                      <Option value="body">body</Option>
                                      <Option value="form">form-data</Option>
                                      <Option value="header">header</Option>
                                      <Option value="path">path</Option>
                                      <Option value="string">string</Option>
                                      <Option value="int">integer</Option>
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col span={4}>                                    
                                  <Form.Item                                    
                                    name={[inputName, "unit"]}
                                    normalize={(value) => value?.trim() || ""}
                                    rules={[
                                      { required: true, message: "Unit is required" },
                                      ...(arrayFormatStates[inputKey]
                                        ? [
                                            {
                                              pattern: /^\[.+\]$/,
                                              message: "Unit must be in [item1, item2, item3] format",
                                            },
                                          ]
                                        : [
                                            {
                                              pattern: /^[a-zA-Z0-9]+$/,
                                              message: "Unit must contain only letters and numbers",
                                            },
                                          ]),
                                    ]}
                                    tooltip={{
                                      title: arrayFormatStates[inputKey] ? (
                                        <div>
                                          <div><strong>Unit Input Format:</strong></div>
                                          <div>• Use square brackets: [item1, item2, item3]</div>
                                          <div>• Separate items with commas</div>
                                          <div>• Examples: [1,2,3] or [kg, lbs, oz]</div>
                                        </div>
                                      ) : (
                                        <div>
                                          <div><strong>Unit Input Format:</strong></div>
                                          <div>• Enter a simple string</div>
                                          <div>• Examples: kg, meters, pieces</div>
                                        </div>
                                      ),
                                      icon: <QuestionCircleOutlined />,
                                    }}
                                  >
                                    <Input
                                      placeholder={
                                        arrayFormatStates[inputKey]
                                          ? "Enter unit in [item1, item2, item3] format"
                                          : "Enter unit as string (e.g., kg, meters)"
                                      }
                                      allowClear
                                    />
                                  </Form.Item>
                                  <Form.Item>
                                    <Checkbox
                                      checked={arrayFormatStates[inputKey] || false}
                                      onChange={(e) => setArrayFormatStates(prev => ({
                                        ...prev,
                                        [inputKey]: e.target.checked
                                      }))}
                                    >
                                      Enum
                                    </Checkbox>
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item name={[inputName, "label"]} rules={[{ required: true }]}>
                                    <Input placeholder="Label" />
                                  </Form.Item>
                                </Col>
                                <Col span={2}>
                                  <Button
                                    icon={<MinusCircleOutlined />}
                                    danger
                                    onClick={() => removeInput(inputName)}
                                  />
                                </Col>
                              </Row>
                            ))}
                            <Form.Item>
                              <Button type="dashed" onClick={() => addInput()} icon={<PlusOutlined />} block>
                                Add Expected Input
                              </Button>
                            </Form.Item>
                          </>
                        )}
                      </Form.List>
                    </Card>

                    <Card
                      type="inner"
                      title={
                        <div style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                          <span style={{ color: "red" }}>*</span>
                          <span>Expected Output</span>
                          <span>(<span style={{ color: "red" }}>at least one</span>)</span>
                        </div>
                      }
                      style={{ marginBottom: 12 , height:'auto' }}
                    >
                      <Form.List
                        name={[field.name, "expectedOutput"]}
                        rules={[
                          {
                            validator: async (_, value) => {
                              if (!value || value.length < 1) {
                                return Promise.reject(new Error("At least one output is required"));
                              }
                            },
                          },
                        ]}
                      >
                        {(outputFields, { add: addOutput, remove: removeOutput }) => (
                          <>
                            {outputFields.map(({ key: outputKey, name: outputName }) => (
                              <Row gutter={16} key={outputKey}>
                                <Col span={4}>
                                  <Form.Item name={[outputName, "name"]} rules={[{ required: true }]}>
                                    <Input placeholder="Name" />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item name={[outputName, "format"]} rules={[{ required: true }]}>
                                    <Input placeholder="Format" />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item name={[outputName, "in"]}>
                                    <Select placeholder="In">
                                      <Option value="query">query</Option>
                                      <Option value="body">body</Option>
                                      <Option value="form">form-data</Option>
                                      <Option value="header">header</Option>
                                      <Option value="path">path</Option>
                                      <Option value="string">string</Option>
                                      <Option value="int">integer</Option>
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item                                    
                                    name={[outputName, "unit"]}
                                    normalize={(value) => value?.trim() || ""}
                                    rules={[
                                      { required: true, message: "Unit is required" },
                                      ...(arrayFormatOutputStates[outputKey]
                                        ? [
                                            {
                                              pattern: /^\[.+\]$/,
                                              message: "Unit must be in [item1, item2, item3] format",
                                            },
                                          ]
                                        : [
                                            {
                                              pattern: /^[a-zA-Z0-9]*$/,
                                              message: "Unit must contain only letters and numbers",
                                            },
                                          ]),
                                    ]}
                                    tooltip={{
                                      title: arrayFormatOutputStates[outputKey] ? (
                                        <div>
                                          <div><strong>Unit Input Format:</strong></div>
                                          <div>• Use square brackets: [item1, item2, item3]</div>
                                          <div>• Separate items with commas</div>
                                          <div>• Examples: [1,2,3] or [kg, lbs, oz]</div>
                                        </div>
                                      ) : (
                                        <div>
                                          <div><strong>Unit Input Format:</strong></div>
                                          <div>• Enter a simple string</div>
                                          <div>• Examples: kg, meters, pieces</div>
                                        </div>
                                      ),
                                      icon: <QuestionCircleOutlined />,
                                    }}
                                  >
                                    <Input
                                      placeholder={
                                        arrayFormatOutputStates[outputKey]
                                          ? "Enter unit in [item1, item2, item3] format"
                                          : "Enter unit as string (e.g., kg, meters)"
                                      }
                                      allowClear
                                    />
                                  </Form.Item>
                                  <Form.Item>
                                    <Checkbox
                                      checked={arrayFormatOutputStates[outputKey] || false}
                                      onChange={(e) => setArrayFormatOutputStates(prev => ({
                                        ...prev,
                                        [outputKey]: e.target.checked
                                      }))}
                                    >
                                      Enum
                                    </Checkbox>
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item name={[outputName, "label"]} rules={[{ required: true }]}>
                                    <Input placeholder="Label" />
                                  </Form.Item>
                                </Col>
                                <Col span={2}>
                                  <Button
                                    icon={<MinusCircleOutlined />}
                                    danger
                                    onClick={() => removeOutput(outputName)}
                                  />
                                </Col>
                              </Row>
                            ))}
                            <Form.Item>
                              <Button type="dashed" onClick={() => addOutput()} icon={<PlusOutlined />} block>
                                Add Expected Output
                              </Button>
                            </Form.Item>
                          </>
                        )}
                      </Form.List>
                    </Card>
                  </>
                ),
              }))}
            />
            <Form.Item style={{ marginTop: 16 }}>
              <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block>
                Add Endpoint
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    </div>
  );

  const renderLocation = () => (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <h3>
          Solver Usage Locations <span style={{ color: 'red' }}>*</span>
        </h3>
        <p style={{ color: '#666', marginBottom: 8 }}>
          Specify where your solver will be utilized. At least one location is required.
        </p>
        <p style={{ color: '#999', fontSize: 13, marginBottom: 16 }}>
          Example: A railway related solver may always be used on the same location whereas an air solver could be used in multiple.
        </p>
      </div>

      <Form.List
        name="locations"
        rules={[
          {
            validator: async (_, value) => {
              if (!value || value.length < 1) {
                return Promise.reject(new Error("At least one location is required"));
              }
            },
          },
        ]}
      >
        {(locationFields, { add: addLocation, remove: removeLocation }) => (
          <>
            {locationFields.map(({ key, name }) => (
              <Card key={key} style={{ marginBottom: 16 , height:'auto' }} title={`Location #${name + 1}`}>
                <Row gutter={16}>
                  <Col span={10}>
                    <Form.Item
                      name={[name, "country"]}
                      label="Country"
                      rules={[{ required: true, message: "Country is required" }]}
                    >
                      <Input placeholder="e.g., Portugal" />
                    </Form.Item>
                  </Col>
                  <Col span={10}>
                    <Form.Item
                      name={[name, "code"]}
                      label="Country Code"
                      rules={[{ required: true, message: "Code is required" }]}
                    >
                      <Input placeholder="e.g., PT" />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Button danger icon={<MinusCircleOutlined />} onClick={() => removeLocation(name)}>
                      Remove
                    </Button>
                  </Col>
                </Row>
              </Card>
            ))}
            <Form.Item>
              <Button type="dashed" onClick={() => addLocation()} icon={<PlusOutlined />} block>
                Add Location
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    </div>
  );

  const renderDeployment = () => (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <h3>Deployment Configuration <span style={{ color: 'red' }}>*</span></h3>
        <p style={{ color: '#666', marginBottom: 16 }}>
          Configure Docker and deployment settings for your solver.
        </p>
      </div>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={["deployment", "dockerImage"]}
            label="Docker Image"
            rules={[{ required: true, message: "Docker Image is required" }]}
          >
            <Input placeholder="URL of GitLab/GitHub link or Docker image" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={["deployment", "version"]}
            label="Version/Tag"
            rules={[{ required: true, message: "Version is required" }]}
          >
            <Input placeholder="Version/Tag (e.g., v1.0.0, latest)" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={["deployment", "containerPort"]}
            label="Container Port"
            rules={[{ required: true, message: "Container Port is required" }]}
          >
            <Input placeholder="Container Port (e.g., 8080)" />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  // Get step content based on current step
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderBasicInfo();
      case 1:
        return renderSolvingProcess();
      case 2:
        return renderEndpoints();
      case 3:
        return renderLocation();
      case 4:
        return renderDeployment();
      default:
        return null;
    }
  };

  // Updated Functions for modal and solver management
  const handleDeleteClick = async () => {
    setLoading(true);
    try {
      const response = await GetSoverOnboarding();
      setTimeout(() => {
        setOnboardedSolvers(response);
        setFilteredSolvers(response);
        setLoading(false);
        setIsModalVisible(true);
      }, 500);
    } catch (error) {
      setLoading(false);
      message.error("Failed to fetch onboarded solvers");
    }
  };

  const handleDeleteSolver = async (solverId) => {
    try {
      await DeleteSoverOnboarding(solverId);
      const response = await GetSoverOnboarding();
      setOnboardedSolvers(response);
      setFilteredSolvers(response);
      message.success(`Solver deleted successfully`);
    } catch (error) {
      message.error("Failed to delete solver");
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSearchText("");
    setFilteredSolvers([]);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    const filtered = onboardedSolvers.filter(
      (solver) =>
        solver.name.toLowerCase().includes(value.toLowerCase()) ||
        solver.description.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredSolvers(filtered);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Space>
          <span style={{ fontWeight: "bold" }}>{text}</span>
          <Tag color="blue">v{record.version}</Tag>
        </Space>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (text) => <Tag color="green">{text}</Tag>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "50%",
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (_, record) => (
        <Popconfirm
          title="Delete Solver"
          description="Are you sure you want to delete this solver?"
          onConfirm={() => handleDeleteSolver(record.id)}
          okText="Yes"
          cancelText="No"
          okType="danger"
        >
          <Button type="primary" danger icon={<DeleteOutlined />}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Layout>
      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Solver Onboarding - Step by Step</span>
            {role === "developer" && (
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                onClick={handleDeleteClick}
                loading={loading}
              >
                Manage Solvers
              </Button>
            )}
          </div>
        }
        bordered
        style={{
          height:'auto',
          width: "100%",
          padding: 20,
          borderRadius: 10,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          backgroundColor: "#ffffff",
        }}
      >
        <Form
          form={form}
          initialValues={{ tenants: tenant ? [tenant] : ["user"] 
          }}
          layout="vertical"
          style={{ width: '100%' }}
        >
          {/* Progress Steps */}
          <div style={{ marginBottom: 32 }}>
            <Steps 
              current={currentStep} 
              size="small"
              items={steps.map((step, index) => ({
                ...step,
                status: stepValidation[index] 
                  ? 'finish' 
                  : index === currentStep 
                    ? 'process' 
                    : 'wait',
                icon: stepValidation[index] 
                  ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  : index === currentStep
                    ? <ExclamationCircleOutlined style={{ color: '#1890ff' }} />
                    : step.icon
              }))}
            />
          </div>

          {/* Step Content */}
          <div style={{ 
            minHeight: '400px', 
            padding: '24px 0',
            borderRadius: '8px',
            backgroundColor: '#fafafa',
            marginBottom: '24px'
          }}>
            {getStepContent(currentStep)}
          </div>

          {/* Navigation Buttons */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderTop: '1px solid #f0f0f0',
            paddingTop: '16px'
          }}>
            <Button 
              onClick={handlePrevious} 
              disabled={currentStep === 0}
              size="large"
            >
              Previous
            </Button>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#666', fontSize: '14px' }}>
                Step {currentStep + 1} of {steps.length}
              </span>
            </div>

            {currentStep < steps.length - 1 ? (
              <Button 
                type="primary" 
                onClick={handleNext}
                size="large"
              >
                Next
              </Button>
            ) : (
              <Button 
                type="primary" 
                onClick={handleSubmit}
                size="large"
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              >
                Submit
              </Button>
            )}
          </div>
        </Form>
      </Card>

      {/* Modal for managing solvers */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>Manage Onboarded Solvers</span>
          </Space>
        }
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={[
          <Button key="close" onClick={handleModalClose}>
            Close
          </Button>,
        ]}
        width={1000}        
      >
        <div style={{ marginBottom: "16px" }}>
          <Input
            placeholder="Search by name or description..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            style={{ width: "100%" }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredSolvers}
          rowKey="id"
          pagination={{
            pageSize: 8,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} solvers`,
          }}
          locale={{
            emptyText: searchText ? `No solvers found matching "${searchText}"` : "No onboarded solvers found",
          }}
        />
      </Modal>
    </Layout>
  );
};

export default CreateNewEndpoints;